import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { blockLabel, canSend, ccWithout, DEFAULT_SENDER, senderOptions, listQuery, loadListState, mailTextChoice, MAIL_TEXT_CHOICES, routeParam, isTextBlock, moveBlock, setMailTextChoice, QUILL_FORMATS, QUILL_MODULES, statusLabel, toggleHidden, updateBlock } from "../../helpers/partnerReports";
import { DataBlockEditor } from "./DataBlockEditor";

// Éditeur d'un reporting partenaire : blocs (texte riche / chiffres), objet, aperçu, envoi.
export function ReportEditor() {
    const params = useParams();
    const partner = routeParam(params.partner);
    const period = routeParam(params.period);
    const [view, setView] = useState(null);
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [preview, setPreview] = useState("");
    const [busy, setBusy] = useState("");
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [pdfAt, setPdfAt] = useState(0); // génération du PDF (le lien signé expire au bout de 15 min)
    const [pdfMissing, setPdfMissing] = useState(""); // motif affiché dans la fenêtre « PDF non généré »
    const [sendAfterCheck, setSendAfterCheck] = useState(false); // fenêtre ouverte depuis « Envoyer »
    const [senders, setSenders] = useState([]);
    const [sender, setSender] = useState(() => { try { return localStorage.getItem("partnerReportSender") || DEFAULT_SENDER; } catch { return DEFAULT_SENDER; } });
    const [testTo, setTestTo] = useState([]); // destinataires du test (utilisateurs pikachu)
    const [testModal, setTestModal] = useState(false);
    const openTestModal = () => {
        if (!testTo.length) { // par défaut : l'utilisateur connecté
            let login = "";
            try { login = JSON.parse(localStorage.getItem("token")).login || ""; } catch { /* session illisible */ }
            const me = senders.find((o) => o.login && o.login === login);
            if (me) setTestTo([me.email]);
        }
        setTestModal(true);
    };
    const toggleTestTo = (email) => setTestTo((l) => (l.includes(email) ? l.filter((e) => e !== email) : [...l, email]));
    const chooseSender = (email) => { setSender(email); try { localStorage.setItem("partnerReportSender", email); } catch { /* stockage indisponible */ } };

    useEffect(() => {
        apiFetch(API.back + "/back-users", { market: false })
            .then((res) => (res.ok ? res.json() : []))
            .then((users) => setSenders(senderOptions(users)))
            .catch(() => setSenders([]));
    }, []);

    const q = `partner=${encodeURIComponent(partner)}&period=${encodeURIComponent(period)}`;

    const loadPreview = useCallback(() => apiFetch(`${API.back}/partner-report-preview?${q}&format=email`, { market: false })
        .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
        .then((p) => setPreview(p.html || ""))
        .catch((e) => setPreview(`<p style="font-family:sans-serif;color:#991b1b">Aperçu indisponible : ${e.message}</p>`)), [q]);

    const load = useCallback(() => apiFetch(`${API.back}/partner-report?${q}`, { market: false })
        .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
        .then((v) => { setView(v); setDraft({ subject: v.report.subject, blocks: v.report.blocks || [] }); setDirty(false); setPdfUrl(""); loadPreview(); })
        .catch((e) => setError("Rapport indisponible : " + e.message)), [q, loadPreview]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        window.onbeforeunload = dirty ? () => "Modifications non enregistrées" : null;
        return () => { window.onbeforeunload = null; };
    }, [dirty]);

    const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); setPdfUrl(""); };
    const changeBlocks = (fn) => change({ blocks: fn(draft.blocks) });

    const save = async () => {
        setBusy("save"); setError(null); setNotice(null);
        const res = await apiFetch(API.back + "/partner-report", { method: "PUT", market: false,
            body: JSON.stringify({ partner, period, subject: draft.subject, blocks: draft.blocks, updatedAt: view.report.updatedAt }) });
        setBusy("");
        if (res.status === 409) { setError("Le rapport a changé entre-temps (régénération ou autre onglet) : recharge la page."); return false; }
        if (!res.ok) { setError(await readError(res)); return false; }
        const r = await res.json();
        setView((v) => ({ ...v, report: r }));
        setDirty(false);
        setNotice("Enregistré.");
        loadPreview();
        return r;
    };

    const regenerate = async (texts) => {
        if (dirty && !window.confirm("Des modifications ne sont pas enregistrées : elles seront perdues. Continuer ?")) return;
        if (texts && !window.confirm("Régénérer aussi les textes ? Tes retouches de texte seront remplacées.")) return;
        const [, kind] = period.split("#");
        setBusy("regen"); setError(null);
        const res = await apiFetch(API.back + "/partner-report-regenerate", { method: "POST", market: false,
            body: JSON.stringify({ partner, kind, period: period.split("#")[0], regenerateTexts: texts }) });
        if (!res.ok) { setBusy(""); setError(await readError(res)); return; }
        setNotice("Régénération lancée : rechargement dans 20 secondes…");
        setTimeout(() => { setBusy(""); load(); }, 20000);
    };

    // Envoi réel sans avoir généré (donc relu) le PDF de cette version : on prévient d'abord.
    const askSend = () => {
        if (!pdfUrl) {
            setSendAfterCheck(true);
            setPdfMissing("Tu n'as pas généré le PDF de cette version du rapport : il partira en pièce jointe sans avoir été relu.");
            return;
        }
        send(false);
    };
    const closePdfModal = () => { setPdfMissing(""); setSendAfterCheck(false); };

    const send = async (test) => {
        let current = view.report;
        if (dirty) {
            const saved = await save();
            if (!saved) return;
            current = saved;
        }
        const cfg = view.config || {};
        let confirmResend = false;
        if (!test) {
            const dest = [...(cfg.to || []), ...ccWithout(cfg.cc, sender).map((c) => c + " (Cc)")].join("\n");
            if (!window.confirm(`Envoyer « ${draft.subject} » de ${sender} à :\n${dest}`)) return;
            if (current.status === "sent") {
                if (!window.confirm("Ce rapport a DÉJÀ été envoyé. Le renvoyer quand même ?")) return;
                confirmResend = true;
            }
        }
        setBusy(test ? "test" : "send"); setError(null); setNotice(null);
        const res = await apiFetch(API.back + "/partner-report-send", { method: "POST", market: false, body: JSON.stringify({ partner, period, test, confirmResend, sender, testRecipients: test ? testTo : [], updatedAt: current.updatedAt }) });
        setBusy("");
        if (res.status === 409 && !test) { setError("Le rapport a changé depuis que tu l'as ouvert (ou un envoi est en cours) : recharge et relis avant d'envoyer."); load(); return; }
        if (!res.ok) { setError(await readError(res)); load(); return; }
        setNotice(test ? (testTo.length ? `Test envoyé à ${testTo.join(", ")}.` : "Test envoyé sur ton adresse.") : "Rapport envoyé.");
        load();
    };

    const openPDF = async () => {
        if (dirty && !(await save())) return;
        setBusy("pdf"); setError(null);
        const res = await apiFetch(`${API.back}/partner-report-preview?${q}&format=pdf`, { market: false });
        setBusy("");
        if (!res.ok) { setError(await readError(res)); return; }
        const p = await res.json();
        // Pas de window.open après un appel réseau (bloqué comme pop-up) : lien à cliquer, valable 15 min.
        setPdfUrl(p.pdfUrl || "");
        setPdfAt(Date.now());
        setPdfMissing("");
    };

    // « Ouvrir le PDF » sans PDF à jour : on explique au lieu d'ouvrir une page d'erreur S3 ou un ancien PDF.
    const PDF_TTL_MS = 14 * 60 * 1000;
    const checkPDF = (e) => {
        let why = "";
        if (!pdfUrl) why = "Le PDF n'a pas encore été généré pour cette version du rapport (ou le rapport a été modifié depuis).";
        else if (Date.now() - pdfAt > PDF_TTL_MS) why = "Le lien du PDF a expiré (il n'est valable que 15 minutes).";
        if (why) { e.preventDefault(); setPdfMissing(why); }
    };

    if (error && !view) return <Container fluid className="p-0"><Alert color="danger">{error}</Alert></Container>;
    if (!view || !draft) return <Spinner size="sm" />;

    const r = view.report;
    const ok = canSend({ ...r, subject: draft.subject, blocks: draft.blocks }, view.config);
    const sent = r.status === "sent";

    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div>
                    <Link to={"/partner-reports/reports" + listQuery(loadListState(""))} className="small">← Rapports</Link>
                    <h1 className="h3 mb-0">{r.displayName} · {r.periodLabel || r.periodId} <Badge color={sent ? "success" : "secondary"}>{statusLabel(r.status)}</Badge></h1>
                    {sent && <div className="small text-muted">Envoyé le {new Date(r.sentAt).toLocaleString("fr-FR")} par {r.sentBy}{r.sentFrom && ` (de ${r.sentFrom})`}</div>}
                </div>
                <div className="text-nowrap">
                    <Button color="light" className="mr-1" disabled={!!busy || sent} onClick={save}>{busy === "save" ? <Spinner size="sm" /> : "Enregistrer"}</Button>
                    <Button color="light" className="mr-1" disabled={!!busy || sent} onClick={() => regenerate(false)}>Régénérer les chiffres</Button>
                    <Button color="light" className="mr-1" disabled={!!busy || sent} onClick={() => regenerate(true)}>Chiffres + textes</Button>
                    <Button color="light" className="mr-1" disabled={!!busy || !ok.ok} onClick={openTestModal}>{busy === "test" ? <Spinner size="sm" /> : "Envoyer un test"}</Button>
                    <Button color="primary" disabled={!!busy || !ok.ok} title={ok.reason} onClick={askSend}>{busy === "send" ? <Spinner size="sm" /> : sent ? "Renvoyer" : "Envoyer"}</Button>
                </div>
            </div>
            {!ok.ok && <Alert color="secondary" className="py-2">Envoi impossible : {ok.reason}</Alert>}
            {error && <Alert color="danger">{error}</Alert>}
            {notice && <Alert color="success" className="py-2">{notice}</Alert>}
            {(r.warnings || []).map((w) => <Alert key={w} color="warning" className="py-2 mb-2">{w}</Alert>)}
            {r.error && <Alert color="danger">Dernier envoi en échec : {r.error}</Alert>}

            <Row>
                <Col lg={6}>
                    <Card><CardBody>
                        <Label className="font-weight-bold">Objet du mail</Label>
                        <Input value={draft.subject} disabled={sent} onChange={(e) => change({ subject: e.target.value })} />
                        <Label className="font-weight-bold mt-2 mb-1">Expéditeur</Label>
                        <Input type="select" bsSize="sm" value={sender} disabled={sent} onChange={(e) => chooseSender(e.target.value)}>
                            {!senders.some((o) => o.email === sender) && <option value={sender}>{sender}</option>}
                            {senders.map((o) => <option key={o.email} value={o.email}>{o.label}</option>)}
                        </Input>
                        <div className="small text-muted mt-1">Les réponses du partenaire arrivent sur cette adresse.</div>
                        <div className="small text-muted mt-1">À : {(view.config?.to || []).join(", ") || "—"}{ccWithout(view.config?.cc, sender).length > 0 && ` · Cc : ${ccWithout(view.config.cc, sender).join(", ")}`}</div>
                        {draft.blocks.some((b) => b.type === "analysis" || b.type === "actions") && (
                            <div className="mt-2">
                                <Label className="font-weight-bold mb-1">Texte dans le mail</Label>
                                <div>
                                    {MAIL_TEXT_CHOICES.map((c) => (
                                        <Button key={c.value} size="sm" className="mr-1" disabled={sent}
                                            color={mailTextChoice(draft.blocks) === c.value ? "primary" : "light"}
                                            onClick={() => changeBlocks((bs) => setMailTextChoice(bs, c.value))}>{c.label}</Button>
                                    ))}
                                </div>
                                <div className="small text-muted mt-1">L'analyse et le plan d'action restent dans le PDF joint (bouton « Masquer » d'un bloc pour l'en retirer aussi).</div>
                            </div>
                        )}
                    </CardBody></Card>
                    {draft.blocks.map((b, i) => (
                        <Card key={b.id} style={{ opacity: b.hidden ? 0.55 : 1 }}>
                            <CardHeader className="d-flex align-items-center py-2">
                                <span className="font-weight-bold mr-2">{blockLabel(b.type)}</span>
                                {b.unavailable && <Badge color="danger" className="mr-2">Indisponible</Badge>}
                                {b.edited && <Badge color="info" className="mr-2">Retouché</Badge>}
                                <Input bsSize="sm" placeholder="Titre (par défaut)" value={b.title || ""} disabled={sent} style={{ maxWidth: 220 }} className="mr-auto"
                                    onChange={(e) => changeBlocks((bs) => updateBlock(bs, b.id, { title: e.target.value }))} />
                                <Button size="sm" color="link" disabled={sent || i === 0} onClick={() => changeBlocks((bs) => moveBlock(bs, i, -1))}>↑</Button>
                                <Button size="sm" color="link" disabled={sent || i === draft.blocks.length - 1} onClick={() => changeBlocks((bs) => moveBlock(bs, i, 1))}>↓</Button>
                                <Button size="sm" color="link" disabled={sent} onClick={() => changeBlocks((bs) => toggleHidden(bs, b.id))}>{b.hidden ? "Afficher" : "Masquer"}</Button>
                            </CardHeader>
                            {!b.hidden && (
                                <CardBody className="py-2">
                                    {isTextBlock(b.type)
                                        ? <ReactQuill theme="snow" value={b.html || ""} readOnly={sent} modules={QUILL_MODULES} formats={QUILL_FORMATS}
                                            onChange={(html, delta, source) => { if (source === "user") changeBlocks((bs) => updateBlock(bs, b.id, { html })); }} />
                                        : <DataBlockEditor block={b} onChange={(data) => changeBlocks((bs) => updateBlock(bs, b.id, { data }))} />}
                                </CardBody>
                            )}
                        </Card>
                    ))}
                </Col>
                <Col lg={6}>
                    <div style={{ position: "sticky", top: 16 }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <b>Aperçu du mail</b>
                            <div>
                                {dirty && <span className="small text-warning mr-2">enregistre pour mettre à jour l'aperçu</span>}
                                <Button size="sm" color="light" disabled={!!busy} onClick={openPDF}>{busy === "pdf" ? <Spinner size="sm" /> : pdfUrl ? "Régénérer le PDF" : "Générer le PDF"}</Button>
                                <a className={`btn btn-sm ml-1 ${pdfUrl ? "btn-primary" : "btn-outline-secondary"}`} href={pdfUrl || "#"} onClick={checkPDF} target="_blank" rel="noopener noreferrer">Ouvrir le PDF</a>
                            </div>
                        </div>
                        <iframe title="Aperçu" sandbox="" srcDoc={preview} style={{ width: "100%", height: "80vh", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f4f4f5" }} />
                    </div>
                </Col>
            </Row>
            <Modal isOpen={testModal} toggle={() => setTestModal(false)}>
                <ModalHeader toggle={() => setTestModal(false)}>Envoyer un test</ModalHeader>
                <ModalBody>
                    <div className="small text-muted mb-2">Le mail part de {sender}, avec « [TEST] » dans l'objet, uniquement aux personnes cochées. Le rapport reste en brouillon.</div>
                    {senders.map((o) => (
                        <div key={o.email}>
                            <label style={{ cursor: "pointer" }}>
                                <input type="checkbox" className="mr-2" checked={testTo.includes(o.email)} onChange={() => toggleTestTo(o.email)} />
                                {o.label}
                            </label>
                        </div>
                    ))}
                    {!senders.length && <div className="small">Liste des utilisateurs indisponible : le test partira sur ton adresse.</div>}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setTestModal(false)}>Annuler</Button>
                    <Button color="primary" disabled={!!busy || (senders.length > 0 && !testTo.length)} onClick={() => { setTestModal(false); send(true); }}>
                        {senders.length && testTo.length ? `Envoyer à ${testTo.length} personne${testTo.length > 1 ? "s" : ""}` : "Envoyer"}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={!!pdfMissing} toggle={closePdfModal}>
                <ModalHeader toggle={closePdfModal}>PDF non généré</ModalHeader>
                <ModalBody>{pdfMissing} Clique sur « Générer le PDF » (quelques secondes), puis {sendAfterCheck ? "relis-le avant d'envoyer" : "rouvre-le"}.</ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closePdfModal}>{sendAfterCheck ? "Annuler" : "Fermer"}</Button>
                    {sendAfterCheck && <Button color="secondary" disabled={!!busy} onClick={() => { closePdfModal(); send(false); }}>Envoyer quand même</Button>}
                    <Button color="primary" disabled={!!busy} onClick={() => { closePdfModal(); openPDF(); }}>Générer le PDF</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
}
